import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { escapeRegExp } from "lodash";

import { durationFormatHandlerWithHours } from "../../../components/common/duration";

export const getHighlightedParagraphs = (paragraphs, searchTerm, foundWords) => {
  if (!paragraphs || paragraphs.length === 0) {
    return { newPar: [], count: 0 };
  }
  let count = foundWords;
  const newPar = paragraphs.map((part) => {
    const { children, ...rest } = part;
    const words = children[0].words;
    const searchTermEscaped = escapeRegExp(searchTerm);
    const regex = new RegExp(`(${searchTermEscaped})`, "gi");

    const parts = words.flatMap((word) => {
      const matchArray = word.text.split(regex);
      const endOfWord = matchArray.length - 1;
      return matchArray.map((text, index) => {
        const basePart = {
          text,
          isMatch: false,
          index: null,
          start: word.start,
          end: word.end,
          endOfWord: endOfWord === index,
        };

        if (index % 2 === 1) {
          return {
            ...basePart,
            isMatch: true,
            // eslint-disable-next-line no-plusplus
            index: count++,
          };
        }
        return basePart;
      });
    });

    return { parts, ...rest };
  });

  return { newPar, count };
};

export const getHighlightedChapters = (chapter, searchTerm, foundWords) => {
  let count = foundWords;
  const { title, description, paragraphs } = chapter;
  const searchTermEscaped = escapeRegExp(searchTerm);
  const regex = new RegExp(`(${searchTermEscaped})`, "gi");

  const highlightText = (text) =>
    text.split(regex).map((part, index) => ({
      text: part,
      isMatch: index % 2 === 1,
      // eslint-disable-next-line no-plusplus
      index: index % 2 === 1 ? count++ : null,
    }));

  const highlightedTitle = title ? highlightText(title) : title;
  const highlightedDescription = description ? highlightText(description) : description;

  const newPar = paragraphs?.map((part) => {
    const { children, ...rest } = part;
    const words = children[0].words;

    const parts = words.flatMap((word) => {
      const matchArray = word.text.split(regex);
      const endOfWord = matchArray.length - 1;
      return matchArray.map((text, index) => {
        const basePart = {
          text,
          isMatch: false,
          index: null,
          start: word.start,
          end: word.end,
          endOfWord: endOfWord === index,
        };

        if (index % 2 === 1) {
          return {
            ...basePart,
            isMatch: true,
            // eslint-disable-next-line no-plusplus
            index: count++,
          };
        }
        return basePart;
      });
    });

    return { parts, ...rest };
  });

  return { title: highlightedTitle, description: highlightedDescription, newPar, count };
};

export const getSecondsToSkip = (timeToSkip) => {
  const durationObject = moment.duration(timeToSkip).asSeconds();
  return durationObject;
};

export const transformIndexFormat = (currentIndexes) => {
  const transformedArray = currentIndexes.map((item) => {
    const metadata = item.metadata.map((meta) => ({
      title: meta.title || item.title,
      description: meta.synopsis,
      keywords: meta.keywords,
      start: meta.time.start,
      end: null,
      _id: uuidv4(),
    }));

    // Case when there isn't any bookmark (empty index). We create one
    if (metadata.length === 0) {
      const firstSection = {
        title: "First Section (auto-generated)",
        description: "",
        start: 0,
        // end: metadata[0].start - 0.5,
        _id: uuidv4(),
      };
      metadata.unshift(firstSection);
    }

    // Case when there is no first section (first bookmark is more than 00:00:00). We create a first section
    if (metadata.length === 0 || (metadata.length > 0 && metadata[0].start > 0)) {
      const firstSection = {
        title: "First Section (auto-generated)",
        description: "",
        start: 0,
        end: metadata[0].start - 0.5,
        _id: uuidv4(),
      };
      metadata.unshift(firstSection);
    }

    metadata.sort((a, b) => a.start - b.start);
    metadata.forEach((meta, index) => {
      if (index < metadata.length - 1) {
        meta.end = metadata[index + 1].start;
      }
    });
    return { title: item.title, chapters: metadata, _id: item._id };
  });

  return transformedArray;
};
export const buildIndexes = (paragraphs, indexesArray) => {
  const newIndexesArray = [...indexesArray];

  newIndexesArray.forEach((chapters) => {
    chapters.chapters.forEach((chapter) => {
      const subParagraphs = getSubParagraphs(paragraphs, chapter.start, chapter.end);
      chapter.paragraphs = subParagraphs;
    });
  });
  return newIndexesArray;
};

const getSubParagraphs = (paragraphs, start, defaultEnd) => {
  let end = defaultEnd;
  const subParagraphs = [];

  if (!end) {
    const lastIndex = paragraphs.length - 1;
    const lastWord = paragraphs[lastIndex].children[0].words.length - 1;
    end = paragraphs[lastIndex].children[0].words[lastWord].end;
  }
  paragraphs.forEach((paragraph, index) => {
    const isLastParagraph = index === paragraphs.length - 1;
    if (!isLastParagraph) {
      if (paragraph.start > end) {
        return;
      }
      if (start >= paragraph.start && start < paragraphs[index + 1].start) {
        const currSubParagraph = getSubParagraph(paragraph, start, end);
        subParagraphs.push(currSubParagraph);
      }
      if (start < paragraph.start && end < paragraphs[index + 1].start) {
        const currSubParagraph = getLastSubParagraph(paragraph, end);

        subParagraphs.push(currSubParagraph);
      }
      if (start < paragraph.start && paragraphs[index + 1].start < end) {
        subParagraphs.push(paragraph);
      }
    }
    if (isLastParagraph) {
      if (end > paragraph.start && start <= paragraph.start) {
        const currSubParagraph = getLastSubParagraph(paragraph, end);
        subParagraphs.push(currSubParagraph);
      }

      if (start > paragraph.start) {
        const currSubParagraph = getSubParagraph(paragraph, start, end);
        subParagraphs.push(currSubParagraph);
      }
    }
  });
  return subParagraphs;
};

const getSubParagraph = (paragraph, start, end) => {
  let subPar = [];
  let newText = "";
  paragraph.children[0].words.forEach((word, i) => {
    if (start >= word.start) {
      subPar = i !== 0 ? paragraph.children[0].words.slice(i + 1) : paragraph.children[0].words.slice(i);
    }
  });

  subPar.forEach((word, i) => {
    if (end < word.start) {
      subPar = subPar.slice(0, i);
      return;
    }
    newText += `${word.text} `;
  });
  const time = subPar[0] && subPar[0].start;
  const transfPara = {
    ...paragraph,
    startTimecode: time && durationFormatHandlerWithHours(time),
    start: time,
    children: [{ words: subPar, text: newText }],
  };
  return transfPara;
};

const getLastSubParagraph = (paragraph, end) => {
  let subPar = [...paragraph.children[0].words];
  let newText = "";
  for (const [i, word] of paragraph.children[0].words.entries()) {
    if (end < word.start) {
      subPar = paragraph.children[0].words.slice(0, i);
      break;
    }
    newText += `${word.text} `;
  }
  const time = subPar[0].start;
  const transfPara = {
    ...paragraph,
    startTimecode: durationFormatHandlerWithHours(time),
    start: time,
    children: [{ words: subPar, text: newText }],
  };
  return transfPara;
};

export const scrollToCurrentWord = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

export const selectClipText = (idStart, idEnd) => {
  const spanStart = document.getElementById(idStart);
  const spanEnd = document.getElementById(idEnd);

  if (!spanStart || !spanEnd) {
    // id's not found
    return;
  }

  const startNodeText = spanStart.firstChild;
  if (!startNodeText || startNodeText.nodeType !== Node.TEXT_NODE) {
    // not valid text
    return;
  }
  const endNodeText = spanEnd.firstChild;
  if (!endNodeText || endNodeText.nodeType !== Node.TEXT_NODE) {
    // not valid text
    return;
  }

  const range = document.createRange();
  range.setStartAfter(startNodeText);
  range.setEndBefore(endNodeText);
  const selection = window.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
};
